import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'

import { Actions } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { MenuItem } from 'primeng/api'
import { MenuModule } from 'primeng/menu'
import { TooltipModule } from 'primeng/tooltip'

import { FlxButtonModule, FlxFormControlModule, SelectItem } from '@flowx/angular-ui-toolkit'
import { AiCodeSelectionComponent } from '@flowx/designer/modules/ai/agents/developer/components/ai-code-selection/ai-code-selection.component'
import { DeveloperContextType } from '@flowx/designer/modules/ai/agents/developer/store/developer.actions'
import {
  AiDeveloperMenuAction,
  type AiDeveloperMenuTarget,
  buildAiDeveloperMenuItems,
} from '@flowx/designer/modules/ai/common/ai.utils'
import {
  ActionTarget,
  ConversationThreadExecutionPlanActionTargetExpressionType,
  ConversationThreadExecutionPlanActionTargetType,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { AgentAction } from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import {
  AiDeveloperContext,
  ExpressionTargetType,
  FixCodeType,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.dictionary'
import {
  AIContentType,
  AiConversationModel,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { AiLoaderComponent } from '@flowx/designer/modules/ai/modules/external/components/ai-loader/ai-loader.component'
import { AiPreviewAreaComponent } from '@flowx/designer/modules/ai/modules/external/components/preview-area/preview-area.component'
import { scriptingLanguageWithGeneration } from '@flowx/designer/modules/ai/types/ai.types'
import { ScriptingLanguage } from '@flowx/designer/modules/core/dictionary/common.types'
import { ProcessDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { MonacoEditorModule } from '@flowx/designer/modules/monaco-code-editor/editor.module'
import {
  CodeEditorFlxAutocompleteOptions,
  CodeEditorFlxLanguage,
  CodeEditorSelection,
  ModelMarkerWithId,
} from '@flowx/designer/modules/monaco-code-editor/types'
import { ApplicationPermissionsStore } from '@flowx/designer/modules/projects/store/application-permissions.store'
import { SCRIPT_RUNTIME_OPTIONS } from '@flowx/designer/modules/reusable-resources/reusable-functions/models/reusable-function.entity'
import { PreviewMode } from '@flowx/designer/modules/script-builder/constants/expressions.constants'
import { ModalPanelComponent } from '@flowx/designer/shared/components/modal-panel/modal-panel.component'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { selectDataModelEntitiesArray } from '@flowx/designer/shared/store/selectors/data-model.selectors'
import { nodesSelector } from '@flowx/designer/shared/store/selectors/node.selectors'
import {
  selectedTemplateConfig,
  selectedTemplateConfigNodeId,
} from '@flowx/designer/shared/store/selectors/template-config.selectors'
import { getTemplateConfigSelectionId } from '@flowx/designer/shared/utils/template-config.utils'
import { isSelectItem } from '@flowx/shared/utils'

import { ScriptingIconsModule } from '../../scripting-icons.module'
import { CodeEditorComponent } from '../code-editor/code-editor.component'

export type SaveDataEmitterPayload = {
  expression: string
  language?: ScriptingLanguage
  errors: ModelMarkerWithId[]
}

export type RunDataEmitterPayload = { expression: string; language?: ScriptingLanguage }

type LanguageOption = { value: ScriptingLanguage; label: string }
type ScriptingAiContext = AiDeveloperContext & {
  warnings: ModelMarkerWithId[]
  fixCodeType: FixCodeType
  aiContentType: AIContentType
}

export const SCRIPTING_LANGUAGES: LanguageOption[] = Object.keys(ScriptingLanguage).map(
  (scriptingLanguage) => ({
    value: ScriptingLanguage[scriptingLanguage],
    label: scriptingLanguage,
  })
)

export const FlowxLanguageToCodeEditorLanguageMap = {
  [ScriptingLanguage.JAVASCRIPT]: CodeEditorFlxLanguage.Javascript,
  [ScriptingLanguage.PYTHON]: CodeEditorFlxLanguage.Python,
  [ScriptingLanguage.MVEL]: CodeEditorFlxLanguage.Java,
  [ScriptingLanguage.GROOVY]: CodeEditorFlxLanguage.Groovy,
}

@Component({
  selector: 'flx-scripting-edit',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FlxFormControlModule,
    FlxButtonModule,
    MenuModule,
    TooltipModule,
    ScriptingIconsModule,
    CodeEditorComponent,
    AiPreviewAreaComponent,
    AiCodeSelectionComponent,
    MonacoEditorModule,
    AiLoaderComponent,
    ModalPanelComponent,
  ],
  templateUrl: './scripting-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScriptingEditComponent implements OnInit {
  #toastMessageService = inject(ToastMessageService)
  #store = inject(Store)
  #applicationPermissionsStore = inject(ApplicationPermissionsStore)
  #actions$ = inject(Actions)
  #destroyRef = inject(DestroyRef)
  #selectedTemplateNodeId = this.#store.selectSignal(selectedTemplateConfigNodeId)
  #expressionTarget = computed(() => {
    const descriptor = this.conversationTargetDescriptor()
    const nodeId = descriptor?.nodeId ?? this.#selectedTemplateNodeId()
    const elementId =
      descriptor?.templateId ?? getTemplateConfigSelectionId(this.selectedTemplate())

    return nodeId != null && elementId != null ? { nodeId, elementId } : null
  })

  #aiMenuTargetType = computed<AiDeveloperMenuTarget | null>(() => {
    const canUseBusinessRuleAiActions = this.canUseBusinessRuleAiActions()
    const canUseExpressionAiActions = this.canUseExpressionAiActions()
    const expressionTarget = this.#expressionTarget()

    if (canUseBusinessRuleAiActions) {
      return 'business-rule'
    }

    return canUseExpressionAiActions && !!expressionTarget ? 'expression' : null
  })

  showTabs = input<boolean>(false)
  contextType = input<DeveloperContextType>(null)
  conversationTargetDescriptor = input<{
    type: ExpressionTargetType
    index?: number
    nodeId?: number
    actionId?: number
    propName?: string
    templateId?: number
  }>()
  enableAiActions = input(false)
  saveButtonLabel = input('Save')

  save = output<SaveDataEmitterPayload>()
  run = output<RunDataEmitterPayload>()
  previewModeChange = output<PreviewMode>()

  readonly languages: LanguageOption[] = SCRIPTING_LANGUAGES
  readonly scriptRuntimeOptions = SCRIPT_RUNTIME_OPTIONS

  aiSelectionWidget = viewChild('editorSelectionWidget', { read: ElementRef })
  aiSelectionWidgetComponent = viewChild('editorSelectionWidget', {
    read: AiCodeSelectionComponent,
  })

  /**
   * True for business rules, false for the expression editor — see
   * `EditorWithFunctionReferences` for the rule both follow.
   */
  canReferenceFunctions = input(false)

  code = model('')
  language = model<ScriptingLanguage | SelectItem<ScriptingLanguage>>(ScriptingLanguage.JAVASCRIPT)
  processVersionId = input<ProcessDefinition['id']>()
  autocompleteOptions = input<Partial<CodeEditorFlxAutocompleteOptions>>()
  showLangSelect = input(false)
  supportsGraalVM = input(false)
  useGraalVM = model(false)
  savedCode = signal(this.code())
  savedLanguageValue = signal<ScriptingLanguage>(ScriptingLanguage.JAVASCRIPT)
  savedUseGraalVM = signal(this.useGraalVM())
  errors = signal<ModelMarkerWithId[]>([])
  errors$ = toObservable(this.errors)
  hasWarnings = computed(() => !!this.errors().length)
  fixWarningsTooltip = computed(() => (this.hasWarnings() ? '' : 'No active warnings to fix'))
  withFixWarnings = input(false)
  readonly = input(false)
  codeEditorSelection = signal<CodeEditorSelection>(null)
  originalEditorWidth = signal<number>(0)
  nodes = this.#store.selectSignal(nodesSelector)

  languageValue = computed(() => {
    const language = this.language()
    return isSelectItem(language) ? language.value : language
  })

  codeEditorOptions = computed(() => {
    const language = FlowxLanguageToCodeEditorLanguageMap[this.languageValue()]
    return language ? { language } : {}
  })

  codeEditorPreviewOptions = {
    language: 'plaintext',
    readonly: true,
  }

  diffEditorOptions = computed(() => {
    const codeEditorOptions = this.codeEditorOptions()

    return {
      ...codeEditorOptions,
      readonly: true,
      enableSplitViewResizing: true,
      renderSideBySide: true,
      useInlineViewWhenSpaceIsLimited: false,
      renderGutterMenu: false,
      renderOverviewRuler: false,
    }
  })

  diffOriginalModel = computed(() => {
    const code = this.code()
    const language = this.codeEditorOptions().language

    return { code, language }
  })

  diffModifiedModel = computed(() => {
    const code = this.aiGeneratedScript()
    const generatedLanguage = this.aiGeneratedLanguage()
    const language = generatedLanguage
      ? FlowxLanguageToCodeEditorLanguageMap[generatedLanguage]
      : this.diffEditorOptions().language

    return { code, language }
  })

  saveButtonDisabled = computed(
    () =>
      (this.savedCode() === this.code() &&
        this.savedLanguageValue() === this.languageValue() &&
        this.savedUseGraalVM() === this.useGraalVM()) ||
      this.aiReadonly() ||
      this.readonly()
  )

  runButtonVariant = computed(() => {
    const showPreviewArea = this.showPreviewArea()

    return showPreviewArea ? 'primary' : null
  })

  // TODO: rewire to the AI builder harness. The preview area, its header and the diff editor stay
  // mounted for the scripting modal, but everything that used to fill and open them was the
  // agent's outcome on the old conversation. Until there is a new source these read empty, so the
  // area never opens and the plain editor below it is what shows.
  aiGeneratedScript = computed<string | null>(() => null)
  aiGeneratedLanguage = computed<ScriptingLanguage | null>(() => null)
  agentProcessingMessage = computed(() => false)
  aiConversationStreaming = computed(() => false)
  showPreviewArea = computed(() => false)
  showAiLoader = computed(() => false)

  aiActionsOptions = computed<MenuItem[]>(() => {
    const aiMenuTargetType = this.#aiMenuTargetType()

    if (!aiMenuTargetType) {
      return []
    }

    const hasContent = !!this.code()
    const isAiGenerationSupportedForLanguage = scriptingLanguageWithGeneration.includes(
      this.languageValue()
    )
    const context = this.#packageContext()

    const menuActionsByTarget = {
      'business-rule': [
        AiDeveloperMenuAction.FixWarnings,
        AiDeveloperMenuAction.Explain,
        AiDeveloperMenuAction.GenerateOrEdit,
      ],
      'expression': [AiDeveloperMenuAction.FixWarnings, AiDeveloperMenuAction.GenerateOrEdit],
    } satisfies Record<AiDeveloperMenuTarget, AiDeveloperMenuAction[]>

    return buildAiDeveloperMenuItems(
      menuActionsByTarget[aiMenuTargetType],
      {
        target: aiMenuTargetType,
        isReadonly: this.aiReadonly() || this.readonly(),
        isLoading: this.aiConversationStreaming(),
        isAiGenerationSupportedForLanguage,
        hasContent,
        hasWarnings: this.hasWarnings(),
      },
      {
        onFixWarnings: () => this.#fixWarningsWithAi(context, aiMenuTargetType),
        onExplain: () => this.#explainWithAi(context, aiMenuTargetType),
        onGenerate: () =>
          this.#generateOrEditWithAi(
            context,
            aiMenuTargetType,
            hasContent,
            isAiGenerationSupportedForLanguage
          ),
      }
    )
  })

  // TODO: rewire to the AI builder harness. Nothing can hold the editor readonly now that no
  // outcome can be pending against it.
  aiReadonly = computed(() => false)

  hasAiDeveloperPermission = this.#applicationPermissionsStore.designerAiAccess

  canUseBusinessRuleAiActions = computed(
    () =>
      this.hasAiDeveloperPermission() && this.contextType() === DeveloperContextType.BusinessRule
  )

  canUseExpressionAiActions = computed(() => {
    const hasAiDeveloperPermission = this.hasAiDeveloperPermission()
    const contextType = this.contextType()
    const isExpression = [
      DeveloperContextType.Expression,
      DeveloperContextType.ComputedValue,
    ].includes(contextType)

    return hasAiDeveloperPermission && isExpression
  })

  canUseAiActions = computed(() => !!this.#aiMenuTargetType())

  // TODO: rewire to the AI builder harness, with showPreviewArea above.
  previewMode = computed<PreviewMode>(() => 'none')
  previewAreaWidth = computed(() => `calc(100% - ${this.originalEditorWidth()}px)`)
  previousCode = signal('')
  selectedTemplate = this.#store.selectSignal(selectedTemplateConfig)

  constructor() {
    effect(() => {
      this.previewModeChange.emit(this.previewMode())
    })

    effect(() => {
      if (!this.showPreviewArea()) {
        this.previousCode.set(this.code())
      }
    })
  }

  onSave(): void {
    this.save.emit(
      this.showLangSelect()
        ? {
            expression: this.code(),
            language: this.languageValue(),
            errors: this.errors(),
          }
        : {
            expression: this.code(),
            errors: this.errors(),
          }
    )

    this.savedCode.set(this.code())
    this.savedLanguageValue.set(this.languageValue())
    this.savedUseGraalVM.set(this.useGraalVM())
  }

  onRun(): void {
    this.run.emit({
      expression: this.aiGeneratedScript() ?? this.code(),
      language: this.aiGeneratedLanguage() ?? this.languageValue(),
    })
  }

  onErrorsChange(errors: ModelMarkerWithId[]): void {
    this.errors.set(errors)
  }

  onCodeEditorSelectionChange(selection: CodeEditorSelection): void {
    this.codeEditorSelection.set(selection)
  }

  onCodeEditorScroll(): void {
    this.aiSelectionWidgetComponent().selectionMenu()?.hide()
  }

  onDiffLayoutChange(event: {
    original: monaco.editor.EditorLayoutInfo
    modified: monaco.editor.EditorLayoutInfo
  }): void {
    this.originalEditorWidth.set(event.original.width)
  }

  ngOnInit(): void {
    this.savedCode.set(this.code())
    this.savedLanguageValue.set(this.languageValue())
    this.savedUseGraalVM.set(this.useGraalVM())
    this.previousCode.set(this.code())

    if (this.withFixWarnings()) {
      this.#runAiFixWarnings()
    }
  }

  // TODO: rewire to the AI builder harness
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  #fixWarningsWithAi(context: ScriptingAiContext, aiMenuTargetType: AiDeveloperMenuTarget): void {}

  // TODO: rewire to the AI builder harness
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  #explainWithAi(context: AiDeveloperContext, aiMenuTargetType: AiDeveloperMenuTarget): void {}

  // TODO: rewire to the AI builder harness
  #generateOrEditWithAi(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    context: AiDeveloperContext,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    aiMenuTargetType: AiDeveloperMenuTarget,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    hasContent: boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isAiGenerationSupportedForLanguage: boolean
  ): void {}

  // TODO: rewire to the AI builder harness. The success toast went with it: with nothing
  // dispatched there is no fix attempt to announce.
  #runAiFixWarnings(): void {}

  #buildAiActionTarget(aiMenuTargetType: AiDeveloperMenuTarget): ActionTarget {
    const isBusinessRule = aiMenuTargetType === 'business-rule'
    const isExpression = aiMenuTargetType === 'expression'
    const { type, index, nodeId, actionId } = this.conversationTargetDescriptor()
    const expressionTarget = this.#expressionTarget()
    const node = this.nodes().find((node) => node.id === +(expressionTarget?.nodeId ?? nodeId))
    const action = node?.actionDefinitions?.find((action) => action.id === actionId)
    const template = this.selectedTemplate()
    const expressionType =
      type === ExpressionTargetType.Disabled
        ? ConversationThreadExecutionPlanActionTargetExpressionType.Disable
        : type === ExpressionTargetType.Hide
          ? ConversationThreadExecutionPlanActionTargetExpressionType.Hide
          : null
    let name = ''
    if (isBusinessRule) {
      name = `${node?.name} - ${action?.name} - Rule ${index}`
    } else if (isExpression) {
      const templateName = template?.customIdentifier || template?.componentIdentifier
      name = `${node?.name} - ${templateName}${expressionType ? ` - ${expressionType}` : ''}`
    }

    return {
      name,
      targetType: isBusinessRule
        ? ConversationThreadExecutionPlanActionTargetType.BusinessRule
        : ConversationThreadExecutionPlanActionTargetType.Expression,
      expression: isExpression
        ? {
            expressionType:
              expressionType ?? ConversationThreadExecutionPlanActionTargetExpressionType.Unknown,
            nodeId: expressionTarget?.nodeId?.toString(),
            elementId: expressionTarget?.elementId?.toString(),
          }
        : null,
      businessRule: isBusinessRule
        ? {
            nodeType: node?.type,
            nodeId: nodeId.toString(),
            actionId: actionId.toString(),
            index,
          }
        : null,
    }
  }

  #toScriptingLanguage(language: string | null | undefined): ScriptingLanguage | null {
    if (!language) {
      return null
    }

    const normalizedLanguage = language.replace(/^"|"$/g, '').trim()

    switch (normalizedLanguage) {
      case 'Python':
      case 'PYTHON':
        return ScriptingLanguage.PYTHON
      case 'JavaScript':
      case 'JAVASCRIPT':
        return ScriptingLanguage.JAVASCRIPT
      default:
        return null
    }
  }

  #packageContext(): ScriptingAiContext {
    const dataModelEntities = this.#store.selectSignal(selectDataModelEntitiesArray)()

    return {
      code: this.code(),
      language: this.languageValue(),
      warnings: this.errors(),
      datasource: dataModelEntities,
      fixCodeType: FixCodeType.Warning,
      aiContentType: AiConversationModel.getAiContentTypeFromDeveloperContextType(
        this.contextType()
      ),
    }
  }

  #getGenerateContextConfig(
    context: AiDeveloperContext,
    hasContent: boolean,
    isAiGenerationSupportedForLanguage: boolean
  ): {
    action: AgentAction
    message: string
    stepContext: ReturnType<
      | typeof AiConversationModel.generateBusinessRuleContextData
      | typeof AiConversationModel.generateExpressionContextData
      | typeof AiConversationModel.generateComputedValueContextData
    >
  } | null {
    const contextConfigs = {
      business_rule: {
        action:
          hasContent && isAiGenerationSupportedForLanguage
            ? AgentAction.EditBusinessRule
            : AgentAction.BusinessRule,
        message: `I'm here to help you write clean, optimized, and scalable code for your business logic, ensuring your workflows are robust and efficient.`,
        stepContext: AiConversationModel.generateBusinessRuleContextData(
          context,
          this.useGraalVM()
        ),
      },
      expression: {
        action: AgentAction.Expression,
        message: `I'm here to help you write clean, optimized, and scalable code for your business logic, ensuring your workflows are robust and efficient.`,
        stepContext: AiConversationModel.generateExpressionContextData(context),
      },
      computed_value: {
        action: AgentAction.ComputedValue,
        message: `I'm here to help you write clean, optimized, and scalable code for your business logic, ensuring your workflows are robust and efficient.`,
        stepContext: AiConversationModel.generateComputedValueContextData(context),
      },
    }

    return contextConfigs[this.contextType()] || null
  }
}
